import { formatCurrency } from '@mahawi/eshop-common/dist/src/format-currency';
import { getFlag } from '@mahawi/eshop-common/dist/src/get-flag';
import {
  EEventContextAction,
  EEventType,
  type ICountry,
  type ICustomerCurrencyType,
  type IDelivery,
} from '@mahawi/eshop-common/dist/src/types';
import { type Dispatch } from '@reduxjs/toolkit';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';

import { getHead } from '../../head';
import { type ICountryState } from '../reducers/country/types';
import { type ICurrencyState } from '../reducers/currency/types';
import { eventSend } from '../reducers/event/reducer';
import { setActiveCategory } from '../reducers/products/reducer';
import { type IUserState } from '../reducers/user/types';

function DeliveryContainer({
  Currency,
  Country,
  User,
  dispatch,
}: {
  Currency: ICurrencyState;
  Country: ICountryState;
  User: IUserState;
  dispatch: Dispatch;
}): React.JSX.Element {
  useEffect(() => {
    dispatch(
      eventSend({
        type: EEventType.PAGE_DELIVERY,
        action: EEventContextAction.ENTER,
      }),
    );
    dispatch(
      setActiveCategory({
        category: {
          uuid: '',
        },
      }),
    );

    return (): void => {
      dispatch(
        eventSend({
          type: EEventType.PAGE_DELIVERY,
          action: EEventContextAction.LEAVE,
        }),
      );
    };
  }, [dispatch]);

  const { t } = useTranslation();

  useEffect((): void => {
    const { title } = getHead(t, t('navigation.title.delivery'));
    document.title = title;
  }, [t]);

  const rows = (): React.JSX.Element[] => {
    return Country.countries.map((country: ICountry): React.JSX.Element => {
      const currency: ICustomerCurrencyType | undefined =
        Currency.currencies.find(
          (c: ICustomerCurrencyType): boolean =>
            c.isoCode === User.currencyType?.isoCode,
        );

      const deliveries: IDelivery[] = Country.deliveries.filter(
        (d: IDelivery): boolean =>
          d.countryType.isoCode === country.isoCode &&
          d.currencyType.isoCode === User.currencyType?.isoCode,
      );

      const d1: IDelivery | undefined = deliveries.find(
        (d: IDelivery): boolean => d.weight === 1,
      );
      const d2: IDelivery | undefined = deliveries.find(
        (d: IDelivery): boolean => d.weight === 2,
      );
      const d5: IDelivery | undefined = deliveries.find(
        (d: IDelivery): boolean => d.weight === 5,
      );
      const d10: IDelivery | undefined = deliveries.find(
        (d: IDelivery): boolean => d.weight === 10,
      );

      const isActive: boolean =
        !!User.countryType?.isoCode &&
        country.isoCode === User.countryType.isoCode;

      return (
        <tr key={country.isoCode} className={isActive ? 'active' : undefined}>
          <th scope="row">
            {getFlag({ countryTypeIsoCode: country.isoCode })}{' '}
            {`${country.name} (${country.isoCode})`}
            <br />
            <small>{country.delivery.description}</small>
          </th>
          <td>{country.delivery.time}</td>
          <td>{d1 ? formatCurrency(d1.price, currency) : ''}</td>
          <td>{d2 ? formatCurrency(d2.price, currency) : ''}</td>
          <td>{d5 ? formatCurrency(d5.price, currency) : ''}</td>
          <td>{d10 ? formatCurrency(d10.price, currency) : ''}</td>
        </tr>
      );
    });
  };

  return (
    <div className="container container--t delivery">
      <h1>{t('delivery.title')}</h1>

      <p>{t('delivery.feeCalculation')}</p>

      <h2>{t('delivery.feeForCountry')}</h2>

      <table className="deliveryTable">
        <thead>
          <tr>
            <th scope="col">{t('delivery.nameWithCode')}</th>
            <th scope="col">{t('delivery.time')}</th>
            <th scope="col">
              1 kg
              <small>{t('delivery.price')}</small>
            </th>
            <th scope="col">
              2 kg
              <small>{t('delivery.price')}</small>
            </th>
            <th scope="col">
              5 kg
              <small>{t('delivery.price')}</small>
            </th>
            <th scope="col">
              10 kg
              <small>{t('delivery.price')}</small>
            </th>
          </tr>
        </thead>
        <tbody>{rows()}</tbody>
      </table>
    </div>
  );
}

const mapStateToProps = ({ Currency, Country, User }) => ({
  Currency,
  Country,
  User,
});

export default connect(mapStateToProps)(DeliveryContainer);
